import { BreakpointObserver } from '@angular/cdk/layout';
import { Injectable, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Observable, distinctUntilChanged, map, shareReplay } from 'rxjs';

/** Viewport widths in CSS px (OEM-7023). Keep in sync with `$anc-breakpoints` in styles/_anc-breakpoints.scss. */
export const ANC_BREAKPOINTS = {
    compact: 600,
    expanded: 840,
    laptop: 1200,
    large: 1600
} as const;

/** Layout class for the current viewport width. `mobile` (< 600px) is outside the supported range. */
export type AncViewportClass = 'mobile' | 'compact' | 'expanded' | 'laptop' | 'large';

const ANC_VIEWPORT_QUERIES = {
    compact: `(min-width: ${ANC_BREAKPOINTS.compact}px)`,
    expanded: `(min-width: ${ANC_BREAKPOINTS.expanded}px)`,
    laptop: `(min-width: ${ANC_BREAKPOINTS.laptop}px)`,
    large: `(min-width: ${ANC_BREAKPOINTS.large}px)`
} as const;

const ANC_VIEWPORT_ORDER: readonly AncViewportClass[] = ['mobile', 'compact', 'expanded', 'laptop', 'large'];

/** True when `current` is narrower than `threshold`, e.g. isAncViewportBelow('compact', 'expanded') → true. */
export function isAncViewportBelow(current: AncViewportClass, threshold: AncViewportClass): boolean {
    return ANC_VIEWPORT_ORDER.indexOf(current) < ANC_VIEWPORT_ORDER.indexOf(threshold);
}

/** Maps min-width query matches to a viewport class. A width exactly on a breakpoint belongs to the larger class. */
export function toAncViewportClass(matches: (query: string) => boolean): AncViewportClass {
    if (matches(ANC_VIEWPORT_QUERIES.large)) return 'large';
    if (matches(ANC_VIEWPORT_QUERIES.laptop)) return 'laptop';
    if (matches(ANC_VIEWPORT_QUERIES.expanded)) return 'expanded';
    if (matches(ANC_VIEWPORT_QUERIES.compact)) return 'compact';
    return 'mobile';
}

/**
 * Current viewport class, for the few places where code (not CSS) must react to width:
 * the nav drawer, the filter drawer and the < 600px notice.
 *
 * Layout itself stays in CSS. Never use this to render a different component per
 * viewport, or input and selection state would be lost on rotate/resize.
 */
@Injectable({
    providedIn: 'root'
})
export class AncViewportService {
    private readonly breakpointObserver = inject(BreakpointObserver);

    /** Emits the current class on subscribe, then only when the class changes. */
    readonly viewportClass$: Observable<AncViewportClass> = this.breakpointObserver
        .observe(Object.values(ANC_VIEWPORT_QUERIES))
        .pipe(
            map((state) => toAncViewportClass((query) => state.breakpoints[query] ?? false)),
            distinctUntilChanged(),
            shareReplay({ bufferSize: 1, refCount: false })
        );

    readonly viewportClass = toSignal(this.viewportClass$, {
        initialValue: toAncViewportClass((query) => this.breakpointObserver.isMatched(query))
    });

    /** True below 1200px: compact tablet, expanded tablet or narrower. */
    readonly isTabletOrBelow = computed(() => isAncViewportBelow(this.viewportClass(), 'laptop'));
}
